import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditTextareaField } from '../../../EditTextareaField';
import {
  type AccountSummaryData,
  GET_ACCOUNT_SUMMARY
} from '../queries/accountSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountSummaryData;
}

const TEXT_FIELDS: FieldMapping[] = [
  { apiField: 'description', formField: 'description' },
  { apiField: 'nextSteps', formField: 'nextSteps' },
  { apiField: 'lastCompletedStep', formField: 'complementedStep' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountSummaryData,
  initial: AccountSummaryData
): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  for (const { apiField, formField } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    if (currentVal !== initialVal) {
      changes[apiField] = currentVal === '' ? null : currentVal;
    }
  }

  return changes;
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        description
        nextSteps
        lastCompletedStep
      }
    }
  }
`;

export interface EditTextsDialogProps {
  accountId: string;
  initialValue: AccountSummaryData;
  isOpen: boolean;
  onChange: (data: AccountSummaryData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountSummaryData;
}

export function EditTextsDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditTextsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_SUMMARY, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(TEXT_FIELDS, value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountSummaryData>(
    field: K,
    fieldValue: AccountSummaryData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save texts');
      } else {
        toast.success('Texts saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving texts:', error);
      toast.error('Failed to save texts');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Texts"
    >
      <div className="flex flex-col gap-4">
        <EditTextareaField
          label="Description"
          onChange={v => {
            updateField('description', v);
          }}
          rows={4}
          value={value.description}
        />
        <EditTextareaField
          label="Next Steps"
          onChange={v => {
            updateField('nextSteps', v);
          }}
          rows={4}
          value={value.nextSteps}
        />
        <EditTextareaField
          label="Last Completed Step"
          onChange={v => {
            updateField('complementedStep', v);
          }}
          rows={4}
          value={value.complementedStep}
        />
      </div>
    </EditDialog>
  );
}
